import React from 'react';
import { useDropzone } from 'react-dropzone';
import { IoDocumentTextOutline } from 'react-icons/io5';
import styled from 'styled-components';

import {
  CircleContainer,
  ImageConstraintsText,
  ImageDropzoneContainer,
  ImageIconContainer,
  ImageUploadContainer,
  ImageUploadInstructionText
} from '../../components/ImageUploadWithPreviewV3';
import { FormField, FormItem, FormMessage } from '../../components/ui/Form';
import { Palette } from '../../components/utils';
import {
  DOCUMENT_ACCEPTED_MIME_TYPES,
  DOCUMENT_MAX_VIDEO_SIZE_BYTES,
  DOCUMENT_UPLOAD_TYPE_HINT,
  getDocumentMaxSizeBytes,
  getDocumentMaxSizeError,
  isDocumentVideoFile
} from './constants';
import { DocumentDropzoneHint } from './documentWizardStyles';
import DocumentUploadedFileRow from './DocumentUploadedFileRow';

const DocumentIconContainer = styled(CircleContainer)`
  width: 4rem;
  height: 4rem;
`;

function AddDocumentUploadStep({ t, form, file, onFileChange }) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: DOCUMENT_ACCEPTED_MIME_TYPES,
    // Allow video ceiling; non-video size is enforced below.
    maxSize: DOCUMENT_MAX_VIDEO_SIZE_BYTES,
    multiple: false,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections?.length) {
        form.setValue('file', null, { shouldDirty: true, shouldValidate: false });

        const hasInvalidType = fileRejections.some((rejection) =>
          rejection.errors?.some((error) => error.code === 'file-invalid-type')
        );
        if (hasInvalidType) {
          form.setError('file', {
            type: 'file-invalid-type',
            message: t('Invalid file type')
          });
          return;
        }

        const tooLargeRejection = fileRejections.find((rejection) =>
          rejection.errors?.some((error) => error.code === 'file-too-large')
        );
        if (tooLargeRejection) {
          form.setError('file', {
            type: 'file-size',
            message: t(getDocumentMaxSizeError(tooLargeRejection.file))
          });
          return;
        }

        form.setError('file', {
          type: 'file-invalid-type',
          message: t('Invalid file type')
        });
        return;
      }

      const nextFile = acceptedFiles?.[0] || null;
      if (nextFile && nextFile.size > getDocumentMaxSizeBytes(nextFile)) {
        form.setValue('file', null, { shouldDirty: true, shouldValidate: false });
        form.setError('file', {
          type: 'file-size',
          message: t(getDocumentMaxSizeError(nextFile))
        });
        return;
      }

      // FE currently only allows .mp4 for video (reject other video MIME if somehow accepted).
      if (nextFile?.type?.startsWith('video/') && !isDocumentVideoFile(nextFile)) {
        form.setValue('file', null, { shouldDirty: true, shouldValidate: false });
        form.setError('file', {
          type: 'file-invalid-type',
          message: t('Invalid file type')
        });
        return;
      }

      onFileChange(nextFile);
    }
  });

  const clearFile = (event) => {
    event.stopPropagation();
    onFileChange(null);
  };

  return (
    <FormField
      control={form.control}
      name="file"
      render={() => (
        <FormItem alt>
          {file ? (
            <DocumentUploadedFileRow
              t={t}
              file={file}
              onClearFile={clearFile}
            />
          ) : (
            <ImageDropzoneContainer {...getRootProps()}>
              <input {...getInputProps()} />
              <ImageUploadContainer
                direction="column"
                gapX="24px"
                alignItems="center"
                justify="center">
                <DocumentIconContainer $hasImage={false} aria-hidden>
                  <ImageIconContainer>
                    <IoDocumentTextOutline size="1.5rem" color={Palette.HAZY_BLUE} />
                  </ImageIconContainer>
                </DocumentIconContainer>
                <div style={{ textAlign: 'center' }}>
                  <ImageUploadInstructionText>
                    {isDragActive
                      ? t('Drop your document here…', { defaultValue: 'Drop your document here…' })
                      : t('Drag and drop your document', {
                          defaultValue: 'Drag and drop your document'
                        })}
                  </ImageUploadInstructionText>
                  <ImageConstraintsText>
                    {t('document_upload_constraints', {
                      defaultValue: `${DOCUMENT_UPLOAD_TYPE_HINT} · 10 MB max (docs) · 500 MB max (VID)`
                    })}
                  </ImageConstraintsText>
                  <DocumentDropzoneHint>
                    {t('document_upload_one_file_hint', {
                      defaultValue: 'One file per upload. Who can see it is set per document.'
                    })}
                  </DocumentDropzoneHint>
                </div>
              </ImageUploadContainer>
            </ImageDropzoneContainer>
          )}

          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export default AddDocumentUploadStep;
